import { useEffect, useState } from 'react';
import { type FieldPath, type FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useDebounce } from '../hooks/useDebounce';

// Drives field validation off a debounced value: after the user pauses typing,
// runs `trigger(name)` so the resolver re-evaluates without firing on every keystroke.
// The form should set mode/reValidateMode: 'onSubmit' so RHF doesn't *also* run its
// own per-keystroke revalidation alongside this trigger — RHF's mode disables that
// duplication, it doesn't suppress validation entirely.
export function useDebouncedFieldValidation<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>>(
  name: N,
  debounceMs: number | undefined,
): { isValidating: boolean; isValidated: boolean } {
  const { trigger } = useFormContext<T>();
  const value = useWatch<T>({ name });
  const debouncedValue = useDebounce(value, debounceMs ?? 0);
  const [isValidating, setIsValidating] = useState(false);
  const [validatedValue, setValidatedValue] = useState<unknown>(undefined);

  useEffect(() => {
    if (!debouncedValue || !debounceMs) {
      return;
    }
    let cancelled = false;
    setIsValidating(true);
    trigger(name)
      .catch(() => setIsValidating(false))
      .finally(() => {
        if (!cancelled) {
          setIsValidating(false);
          setValidatedValue(debouncedValue);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedValue, debounceMs, trigger, name]);

  // A validation result is only trustworthy once the value it ran against matches the
  // current live value — otherwise the user has typed more since the debounce fired.
  return { isValidating, isValidated: validatedValue === value };
}
